import React, { Fragment, useEffect, useRef, useState } from 'react'
import Select, { InputActionMeta, SingleValue } from 'react-select'
import debounce from 'lodash/debounce'
import { customSelectOption, customSelectValue } from '../shared/common'
import RecipeVersionSelect from '../recipes/RecipeVersionSelect'
import { useOrderRecipes } from '../../hooks/useOrderRecipes'
import { OrderRecipeGroup, OrderRecipeVersion } from './types'
import { PortionType } from '../recipes/types'

const SEARCH_DEBOUNCE_MS = 500

export interface OrderItemSelection {
  group: OrderRecipeGroup
  version: OrderRecipeVersion
}

interface RecipeSelectOption {
  value: number
  label: string
  imageUrl: string
}

interface Props {
  idx: number
  portionType: PortionType
  currency: { code: string }
  amount: number
  optedOutProductIds: number[]
  selection: OrderItemSelection | null
  removeDisabled: boolean
  onSelect: (idx: number, selection: OrderItemSelection) => void
  onAmountChange: (idx: number, amount: number) => void
  onOptedOutProductToggle: (idx: number, productId: number, included: boolean) => void
  onRemove: (idx: number) => void
}

const recipeLabel = (name: string, price: number, currencyCode: string) =>
  `${name} | ${price.toFixed(2)} ${currencyCode}`

const parseAmount = (text: string) => {
  const parsed = Number(text)
  return Number.isNaN(parsed) ? 0 : parsed
}

const OrderItemRow = ({
  idx, portionType, currency, amount, optedOutProductIds, selection, removeDisabled,
  onSelect, onAmountChange, onOptedOutProductToggle, onRemove
}: Props) => {
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [amountText, setAmountText] = useState(String(amount))

  const debouncedSetSearchQuery = useRef(debounce((value: string) => setSearchQuery(value), SEARCH_DEBOUNCE_MS)).current
  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery])
  useEffect(() => {
    if (parseAmount(amountText) !== amount) setAmountText(String(amount))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount])

  const { groups, loading, loadMore } = useOrderRecipes({ query: searchQuery, portionType })

  const options: RecipeSelectOption[] = groups.map(group => ({
    value: group.id,
    label: recipeLabel(group.name, group.price, currency.code),
    imageUrl: group.image_url
  }))

  const value: RecipeSelectOption | null = selection
    ? {
        value: selection.group.id,
        label: recipeLabel(selection.group.name, selection.version.price, currency.code),
        imageUrl: selection.group.image_url
      }
    : null

  const handleRecipeChange = (option: SingleValue<RecipeSelectOption>) => {
    if (!option) return

    const group = groups.find(candidate => candidate.id === option.value)
    if (!group) return

    const defaultVersion = group.versions.find(version => version.id === group.id) ?? group.versions[0]
    onSelect(idx, { group, version: defaultVersion })

    debouncedSetSearchQuery.cancel()
    setInputValue('')
    setSearchQuery('')
  }

  const handleVersionChange = (versionId: number) => {
    if (!selection) return

    const version = selection.group.versions.find(candidate => candidate.id === versionId)
    if (!version) return

    onSelect(idx, { group: selection.group, version })
  }

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action !== 'input-change') return

    setInputValue(newValue)
    debouncedSetSearchQuery(newValue)
  }

  const amountInputName = `orderItems[${idx}].amount`
  const versionInputId = `orderItems[${idx}].version`
  const optionalProducts = selection?.version.optional_products ?? []

  return (
    <Fragment>
      <div className={`row mb-3 order-item-${idx}`}>
        <div className='col-sm-8'>
          <div className='form-group mb-0'>
            <Select<RecipeSelectOption> className='react-select' classNamePrefix='react-select'
              options={options}
              value={value}
              inputValue={inputValue}
              isLoading={loading}
              filterOption={() => true}
              onInputChange={handleInputChange}
              onMenuScrollToBottom={loadMore}
              onChange={handleRecipeChange}
              components={{
                Option: customSelectOption,
                SingleValue: customSelectValue
              }}
            />
          </div>
        </div>
        <div className='col-sm-3'>
          <div className='form-group mb-0 number-spinner-wrapper'>
            <button
              type='button'
              className='btn number-spinner decrease'
              onClick={() => onAmountChange(idx, Math.max(amount - 1, 0))}
            >
              -
            </button>
            <input className='form-control text-center amount-input'
              type='number'
              min='0'
              name={amountInputName}
              value={amountText}
              onChange={(e) => {
                setAmountText(e.target.value)
                onAmountChange(idx, parseAmount(e.target.value))
              }}
            />
            <button
              type='button'
              className='btn number-spinner increase'
              onClick={() => onAmountChange(idx, amount + 1)}
            >
              +
            </button>
          </div>
        </div>
        <div className='col-sm-1 d-flex align-items-center'>
          <i
            className={`button-delete fa fa-trash-o fa-2x pointer ${removeDisabled ? 'disabled' : ''}`}
            onClick={() => onRemove(idx)}
          />
        </div>
        <div className='col-sm-8 form-group mt-2 mb-0'>
          <label htmlFor={versionInputId}>Version</label>
          <RecipeVersionSelect
            inputId={versionInputId}
            versions={selection?.group.versions ?? []}
            value={selection?.version.id ?? null}
            isDisabled={!selection}
            onChange={handleVersionChange}
          />
        </div>
        {optionalProducts.length > 0 && (
          <div className='col-sm-8 form-group mt-2 mb-0'>
            <label>Optional Products (uncheck to remove)</label>
            <div className="ps-4">
              {optionalProducts.map(product => (
                <fieldset key={product.id} className="form-group boolean mb-0 me-3">
                  <div className="form-check">
                    <input
                      id={`${idx}-${product.id}`}
                      type="checkbox"
                      className="form-check-input boolean"
                      checked={!optedOutProductIds.includes(product.id)}
                      onChange={(e) => onOptedOutProductToggle(idx, product.id, e.target.checked)}
                    />
                    <label className="form-check-label boolean" htmlFor={`${idx}-${product.id}`}>
                      {product.name}
                    </label>
                  </div>
                </fieldset>
              ))}
            </div>
          </div>
        )}
      </div>
      <hr />
    </Fragment>
  )
}

export default OrderItemRow
